/**
 * Única ação do card do plano atual: migrar a periodicidade (mensal ⇄ anual).
 *
 * O bloqueio vem pronto do backend (`annual_lock_until`) — aqui só se decide o
 * que exibir. Com troca de plano aguardando pagamento, a periodicidade também
 * fica travada até o assinante pagar ou cancelar a troca.
 */
import { Button } from '@/components/ui/button';
import { LockedActionHint } from '@/components/subscription/LockedActionHint';
import {
  PENDING_CHANGE_BLOCKS_CYCLE_MESSAGE,
  annualLockToMonthlyMessage,
} from '@/lib/plan-change-messages';
import type { UserSubscription } from '@/services/account.service';

export type BillingCycle = 'monthly' | 'yearly';

interface CurrentPlanCycleActionProps {
  subscription: UserSubscription;
  onMigrate: (targetCycle: BillingCycle) => void;
}

const motivoDoBloqueio = (subscription: UserSubscription, targetCycle: BillingCycle): string | null => {
  if (subscription.pending_plan_id) return PENDING_CHANGE_BLOCKS_CYCLE_MESSAGE;
  if (targetCycle === 'monthly' && subscription.annual_lock_until) {
    return annualLockToMonthlyMessage(subscription.annual_lock_until);
  }
  return null;
};

export function CurrentPlanCycleAction({ subscription, onMigrate }: Readonly<CurrentPlanCycleActionProps>) {
  const targetCycle: BillingCycle = subscription.billing_cycle === 'yearly' ? 'monthly' : 'yearly';
  const bloqueio = motivoDoBloqueio(subscription, targetCycle);

  return (
    <LockedActionHint message={bloqueio}>
      <Button
        variant="outline"
        className="w-full"
        onClick={() => onMigrate(targetCycle)}
      >
        {targetCycle === 'yearly' ? 'Pagamento Anual' : 'Pagamento Mensal'}
      </Button>
    </LockedActionHint>
  );
}
